/**
 * StickmanBody - 火柴人骨骼结构定义
 * @description 定义火柴人各部位的物理属性和关节连接
 */

import planck, { Vec2, Body, Circle, Box } from 'planck';
import type { BodyDef, FixtureDef, RevoluteJointDef } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';

/**
 * 火柴人骨骼部位标识
 */
export type StickmanPartId =
  | 'head'       // 头部
  | 'torso'      // 躯干
  | 'upperArmL'  // 左上臂
  | 'upperArmR'  // 右上臂
  | 'lowerArmL'  // 左前臂
  | 'lowerArmR'  // 右前臂
  | 'upperLegL'  // 左大腿
  | 'upperLegR'  // 右大腿
  | 'lowerLegL'  // 左小腿
  | 'lowerLegR'  // 右小腿;

/**
 * 单个骨骼部位的定义
 */
export interface StickmanPartDef {
  /** 部位标识 */
  id: StickmanPartId;
  /** 相对于父部位的初始位置偏移 */
  offset: Vec2;
  /** 部位尺寸 */
  size: {
    /** 圆形半径（头部使用） */
    radius?: number;
    /** 矩形宽度 */
    width?: number;
    /** 矩形高度（长度） */
    height?: number;
  };
  /** 密度 */
  density: number;
  /** 摩擦系数 */
  friction: number;
  /** 恢复系数（弹性） */
  restitution: number;
}

/**
 * 关节定义
 */
export interface StickmanJointDef {
  /** 关节名称 */
  name: string;
  /** 连接的身体部位 A */
  bodyA: StickmanPartId;
  /** 连接的身体部位 B */
  bodyB: StickmanPartId;
  /** 关节锚点（相对于 bodyA） */
  anchorA: Vec2;
  /** 关节锚点（相对于 bodyB） */
  anchorB: Vec2;
  /** 关节角度下限（弧度） */
  lowerAngle: number;
  /** 关节角度上限（弧度） */
  upperAngle: number;
  /** 最大关节力矩 */
  maxMotorTorque: number;
  /** 是否启用限制 */
  enableLimit: boolean;
}

/**
 * 火柴人骨骼默认配置
 * 基于任务规格：
 * - 头部：圆形，半径 15
 * - 躯干：矩形，宽 20，高 40
 * - 左右上臂、左右前臂：各长 25
 * - 左右大腿、左右小腿：各长 30
 */
export const DEFAULT_STICKMAN_CONFIG: {
  parts: StickmanPartDef[];
  joints: StickmanJointDef[];
} = {
  parts: [
    // 头部
    {
      id: 'head',
      offset: Vec2(0, 0),
      size: { radius: 15 },
      density: 1.0,
      friction: 0.5,
      restitution: 0.3,
    },
    // 躯干
    {
      id: 'torso',
      offset: Vec2(0, -25), // 头部下方 25 像素（头部半径 15 + 间隙 10）
      size: { width: 20, height: 40 },
      density: 1.5,
      friction: 0.5,
      restitution: 0.2,
    },
    // 左上臂
    {
      id: 'upperArmL',
      offset: Vec2(-15, 0), // 躯干左侧
      size: { width: 8, height: 25 },
      density: 0.8,
      friction: 0.5,
      restitution: 0.2,
    },
    // 右上臂
    {
      id: 'upperArmR',
      offset: Vec2(15, 0), // 躯干右侧
      size: { width: 8, height: 25 },
      density: 0.8,
      friction: 0.5,
      restitution: 0.2,
    },
    // 左前臂
    {
      id: 'lowerArmL',
      offset: Vec2(0, -25), // 上臂末端
      size: { width: 6, height: 25 },
      density: 0.6,
      friction: 0.5,
      restitution: 0.2,
    },
    // 右前臂
    {
      id: 'lowerArmR',
      offset: Vec2(0, -25),
      size: { width: 6, height: 25 },
      density: 0.6,
      friction: 0.5,
      restitution: 0.2,
    },
    // 左大腿
    {
      id: 'upperLegL',
      offset: Vec2(-8, -40), // 躯干底部左侧
      size: { width: 10, height: 30 },
      density: 1.0,
      friction: 0.5,
      restitution: 0.2,
    },
    // 右大腿
    {
      id: 'upperLegR',
      offset: Vec2(8, -40), // 躯干底部右侧
      size: { width: 10, height: 30 },
      density: 1.0,
      friction: 0.5,
      restitution: 0.2,
    },
    // 左小腿
    {
      id: 'lowerLegL',
      offset: Vec2(0, -30), // 大腿末端
      size: { width: 8, height: 30 },
      density: 0.8,
      friction: 0.5,
      restitution: 0.2,
    },
    // 右小腿
    {
      id: 'lowerLegR',
      offset: Vec2(0, -30),
      size: { width: 8, height: 30 },
      density: 0.8,
      friction: 0.5,
      restitution: 0.2,
    },
  ],
  joints: [
    // 头部-躯干关节（颈部）
    {
      name: 'neck',
      bodyA: 'torso',
      bodyB: 'head',
      anchorA: Vec2(0, 20), // 躯干顶部
      anchorB: Vec2(0, -15), // 头部底部
      lowerAngle: -0.5, // 约 -30 度
      upperAngle: 0.5,  // 约 30 度
      maxMotorTorque: 50,
      enableLimit: true,
    },
    // 左肩关节
    {
      name: 'shoulderL',
      bodyA: 'torso',
      bodyB: 'upperArmL',
      anchorA: Vec2(-10, 18), // 躯干左上角
      anchorB: Vec2(0, 12.5), // 上臂顶部
      lowerAngle: -Math.PI * 0.75, // 约 -135 度
      upperAngle: Math.PI * 0.5,   // 约 90 度
      maxMotorTorque: 40,
      enableLimit: true,
    },
    // 右肩关节
    {
      name: 'shoulderR',
      bodyA: 'torso',
      bodyB: 'upperArmR',
      anchorA: Vec2(10, 18),
      anchorB: Vec2(0, 12.5),
      lowerAngle: -Math.PI * 0.5,
      upperAngle: Math.PI * 0.75,
      maxMotorTorque: 40,
      enableLimit: true,
    },
    // 左肘关节
    {
      name: 'elbowL',
      bodyA: 'upperArmL',
      bodyB: 'lowerArmL',
      anchorA: Vec2(0, -12.5), // 上臂底部
      anchorB: Vec2(0, 12.5), // 前臂顶部
      lowerAngle: 0,
      upperAngle: Math.PI * 0.5, // 约 90 度（只能向内弯曲）
      maxMotorTorque: 30,
      enableLimit: true,
    },
    // 右肘关节
    {
      name: 'elbowR',
      bodyA: 'upperArmR',
      bodyB: 'lowerArmR',
      anchorA: Vec2(0, -12.5),
      anchorB: Vec2(0, 12.5),
      lowerAngle: -Math.PI * 0.5,
      upperAngle: 0,
      maxMotorTorque: 30,
      enableLimit: true,
    },
    // 左髋关节
    {
      name: 'hipL',
      bodyA: 'torso',
      bodyB: 'upperLegL',
      anchorA: Vec2(-6, -20), // 躯干底部左侧
      anchorB: Vec2(0, 15),   // 大腿顶部
      lowerAngle: -Math.PI * 0.25,
      upperAngle: Math.PI * 0.75,
      maxMotorTorque: 80,
      enableLimit: true,
    },
    // 右髋关节
    {
      name: 'hipR',
      bodyA: 'torso',
      bodyB: 'upperLegR',
      anchorA: Vec2(6, -20),
      anchorB: Vec2(0, 15),
      lowerAngle: -Math.PI * 0.75,
      upperAngle: Math.PI * 0.25,
      maxMotorTorque: 80,
      enableLimit: true,
    },
    // 左膝关节
    {
      name: 'kneeL',
      bodyA: 'upperLegL',
      bodyB: 'lowerLegL',
      anchorA: Vec2(0, -15), // 大腿底部
      anchorB: Vec2(0, 15),  // 小腿顶部
      lowerAngle: 0,
      upperAngle: Math.PI * 0.5, // 只能向后弯曲
      maxMotorTorque: 60,
      enableLimit: true,
    },
    // 右膝关节
    {
      name: 'kneeR',
      bodyA: 'upperLegR',
      bodyB: 'lowerLegR',
      anchorA: Vec2(0, -15),
      anchorB: Vec2(0, 15),
      lowerAngle: 0,
      upperAngle: Math.PI * 0.5,
      maxMotorTorque: 60,
      enableLimit: true,
    },
  ],
};

/**
 * StickmanBody 类
 * 管理火柴人的物理骨骼结构
 */
export class StickmanBody {
  private physicsWorld: PhysicsWorld;
  private bodies: Map<StickmanPartId, Body> = new Map();
  private joints: Map<string, planck.RevoluteJoint> = new Map();
  private position: Vec2;
  private scale: number;

  /**
   * 创建火柴人物理骨骼
   * @param physicsWorld 物理世界实例
   * @param position 初始位置（物理坐标系）
   * @param scale 缩放因子，默认 1
   */
  constructor(physicsWorld: PhysicsWorld, position: Vec2, scale: number = 1) {
    this.physicsWorld = physicsWorld;
    this.position = position;
    this.scale = scale;
    this.createBodyParts();
    this.createJoints();
  }

  /**
   * 创建所有身体部位
   */
  private createBodyParts(): void {
    const world = this.physicsWorld.getWorld();

    for (const partDef of DEFAULT_STICKMAN_CONFIG.parts) {
      // 计算实际位置（缩放后）
      const partPos = Vec2.add(this.position, Vec2.mul(partDef.offset, this.scale));

      // 创建物体定义
      const bodyDef: BodyDef = {
        position: partPos,
        type: 'dynamic',
        fixedRotation: false,
      };

      const body = world.createBody(bodyDef);

      // 创建形状
      let fixtureDef: FixtureDef;

      if (partDef.size.radius) {
        // 圆形（头部）
        fixtureDef = {
          shape: Circle(partDef.size.radius * this.scale),
          density: partDef.density,
          friction: partDef.friction,
          restitution: partDef.restitution,
        };
      } else {
        // 矩形（躯干、四肢）
        const halfWidth = (partDef.size.width ?? 10) * this.scale / 2;
        const halfHeight = (partDef.size.height ?? 20) * this.scale / 2;
        fixtureDef = {
          shape: Box(halfWidth, halfHeight),
          density: partDef.density,
          friction: partDef.friction,
          restitution: partDef.restitution,
        };
      }

      body.createFixture(fixtureDef);
      this.bodies.set(partDef.id, body);
    }
  }

  /**
   * 创建所有关节连接
   */
  private createJoints(): void {
    const world = this.physicsWorld.getWorld();

    for (const jointDef of DEFAULT_STICKMAN_CONFIG.joints) {
      const bodyA = this.bodies.get(jointDef.bodyA);
      const bodyB = this.bodies.get(jointDef.bodyB);

      if (!bodyA || !bodyB) {
        console.warn(`关节 ${jointDef.name} 的身体部位不存在`);
        continue;
      }

      // 计算缩放后的锚点
      const anchorA = Vec2.mul(jointDef.anchorA, this.scale);
      const anchorB = Vec2.mul(jointDef.anchorB, this.scale);

      // 创建关节定义
      const revoluteJointDef: RevoluteJointDef = {
        bodyA,
        bodyB,
        localAnchorA: anchorA,
        localAnchorB: anchorB,
        lowerAngle: jointDef.lowerAngle,
        upperAngle: jointDef.upperAngle,
        maxMotorTorque: jointDef.maxMotorTorque,
        enableLimit: jointDef.enableLimit,
        enableMotor: false,
      };

      const joint = world.createJoint(planck.RevoluteJoint(revoluteJointDef));
      if (joint) {
        this.joints.set(jointDef.name, joint as planck.RevoluteJoint);
      }
    }
  }

  /**
   * 获取指定部位的物理对象
   * @param partId 部位标识
   */
  getBody(partId: StickmanPartId): Body | undefined {
    return this.bodies.get(partId);
  }

  /**
   * 获取所有身体部位
   */
  getAllBodies(): Map<StickmanPartId, Body> {
    return this.bodies;
  }

  /**
   * 获取指定关节
   * @param jointName 关节名称
   */
  getJoint(jointName: string): planck.RevoluteJoint | undefined {
    return this.joints.get(jointName);
  }

  /**
   * 获取头部位置（用于拖拽锚点）
   */
  getHeadPosition(): Vec2 {
    const head = this.bodies.get('head');
    if (!head) return this.position;
    return head.getPosition();
  }

  /**
   * 获取躯干位置（用于整体位置参考）
   */
  getTorsoPosition(): Vec2 {
    const torso = this.bodies.get('torso');
    if (!torso) return this.position;
    return torso.getPosition();
  }

  /**
   * 获取所有部位位置
   */
  getAllPositions(): Map<StickmanPartId, Vec2> {
    const positions = new Map<StickmanPartId, Vec2>();
    for (const [id, body] of this.bodies) {
      positions.set(id, body.getPosition());
    }
    return positions;
  }

  /**
   * 获取所有部位角度
   */
  getAllAngles(): Map<StickmanPartId, number> {
    const angles = new Map<StickmanPartId, number>();
    for (const [id, body] of this.bodies) {
      angles.set(id, body.getAngle());
    }
    return angles;
  }

  /**
   * 销毁火柴人物理骨骼
   */
  destroy(): void {
    const world = this.physicsWorld.getWorld();

    // 先销毁关节
    for (const joint of this.joints.values()) {
      world.destroyJoint(joint);
    }
    this.joints.clear();

    // 再销毁身体部位
    for (const body of this.bodies.values()) {
      world.destroyBody(body);
    }
    this.bodies.clear();
  }

  /**
   * 唤醒所有身体部位
   */
  wakeUpAll(): void {
    for (const body of this.bodies.values()) {
      body.setAwake(true);
    }
  }

  /**
   * 应用力到躯干（用于移动控制）
   * @param forceX x 方向力
   * @param forceY y 方向力
   */
  applyForceToTorso(forceX: number, forceY: number): void {
    const torso = this.bodies.get('torso');
    if (torso) {
      torso.applyForceToCenter(Vec2(forceX, forceY), true);
    }
  }

  /**
   * 设置躯干速度
   * @param vx x 方向速度
   * @param vy y 方向速度
   */
  setTorsoVelocity(vx: number, vy: number): void {
    const torso = this.bodies.get('torso');
    if (torso) {
      torso.setLinearVelocity(Vec2(vx, vy));
    }
  }
}

// 导出类型和工具
export { Vec2 };